/**
 * G16 — visible operator alert when CRS alignment rejects a renderable layer.
 * A blank scene with no surfaced error is an operations incident; this banner
 * complements the per-layer `LayerCrsChip` warning in the layer panel.
 */

import type { ReactNode } from 'react';
import { tokens } from '@/design';
import type { ViewerEvent } from '@geosoft/viewer-sdk';

export type LayerCrsEvent = Extract<ViewerEvent, { type: 'crsCorrectionApplied' }>;

export interface LayerAlignmentAlertBannerProps {
  readonly rejections: ReadonlyArray<{
    readonly layerId: string;
    readonly label: string;
    readonly event: LayerCrsEvent;
  }>;
}

export function LayerAlignmentAlertBanner({
  rejections,
}: LayerAlignmentAlertBannerProps): ReactNode {
  if (rejections.length === 0) return null;

  return (
    <div
      role="alert"
      data-testid="layer-alignment-alert-banner"
      style={{
        margin: tokens.space[3],
        padding: tokens.space[3],
        borderRadius: tokens.radius.md,
        border: `${tokens.border.width.thin}px solid ${tokens.color.semantic.warning}`,
        background: tokens.color.surface[1],
        color: tokens.color.ink.primary,
        fontSize: tokens.font.size.sm,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[2],
          fontWeight: tokens.font.weight.semibold,
          marginBottom: tokens.space[2],
        }}
      >
        <span
          aria-hidden
          style={{ fontFamily: 'Material Symbols Rounded', fontSize: tokens.font.size.lg }}
        >
          warning
        </span>
        CRS alignment failed — scene may appear empty
      </div>
      <ul
        style={{
          margin: 0,
          paddingLeft: tokens.space[5],
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.space[1],
        }}
      >
        {rejections.map(({ layerId, label, event }) => (
          <li key={layerId}>
            <strong>{label}</strong>
            {': '}
            {event.rejected?.reason ?? 'Alignment rejected'}
            {event.rejected?.code ? ` (${event.rejected.code})` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}
